import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import AnalyticsRoundedIcon from '@mui/icons-material/AnalyticsRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded';

import { useStyle } from '../../context/styleContext/styleContext';
import Seo from '../../components/Seo';

const formatNumber = (value) => Number(value || 0).toLocaleString();

const formatDate = (value) => {
  if (!value) return '—';

  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const percentText = (value) => `${Number(value || 0).toFixed(1)}%`;

export default function AdminPluginUsagePage() {
  const { style } = useStyle();
  const token = useMemo(() => localStorage.getItem('token') || '', []);

  const [days, setDays] = useState(90);
  const [overview, setOverview] = useState(null);
  const [detail, setDetail] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const panelBg = style.darkPanel || 'rgba(9, 12, 18, 0.45)';

  const sectionCardSx = {
    p: { xs: 1.5, sm: 2 },
    borderRadius: '14px',
    backgroundColor: panelBg,
    border: '1px solid rgba(255,255,255,0.08)',
  };

  const sharedTextFieldSx = {
    minWidth: 160,
    '& .MuiInputBase-root': {
      color: style.color4,
      backgroundColor: style.color2,
      borderRadius: '8px',
    },
    '& .MuiOutlinedInput-root fieldset': {
      borderColor: style.color2,
    },
    '& .MuiOutlinedInput-root:hover fieldset': {
      borderColor: style.color3,
    },
    '& .MuiOutlinedInput-root.Mui-focused fieldset': {
      borderColor: style.accentGreen,
    },
    '& .MuiInputLabel-root': {
      color: style.color3,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: style.color4,
    },
    '& .MuiSvgIcon-root': {
      color: style.color4,
    },
  };

  const tableCellSx = {
    color: style.color4,
    borderColor: 'rgba(255,255,255,0.08)',
  };

  const headerCellSx = {
    ...tableCellSx,
    color: style.color3,
    fontWeight: 800,
    whiteSpace: 'nowrap',
  };

  const loadOverview = async () => {
    try {
      if (!token) {
        toast.error('You must be logged in as an admin.');
        return;
      }

      setLoadingOverview(true);

      const { data } = await axios.post('/admin/plugin-usage-overview', {
        token,
        days,
      });

      if (data?.error) {
        toast.error(data.error);
        return;
      }

      setOverview(data);

      if (!selectedProduct && data?.products?.length) {
        setSelectedProduct(data.products[0].productName);
      }
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Unable to load plugin usage.');
    } finally {
      setLoadingOverview(false);
    }
  };

  const loadDetail = async (productName = selectedProduct) => {
    try {
      if (!token || !productName) return;

      setLoadingDetail(true);

      const { data } = await axios.post('/admin/plugin-usage-detail', {
        token,
        days,
        productName,
      });

      if (data?.error) {
        toast.error(data.error);
        return;
      }

      setDetail(data);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Unable to load plugin detail.');
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    loadOverview();
  }, [days]);

  useEffect(() => {
    if (selectedProduct) {
      loadDetail(selectedProduct);
    }
  }, [selectedProduct, days]);

  const selectedProductOverview = overview?.products?.find(
    (product) => product.productName === selectedProduct
  );

  const statCards = [
    {
      label: 'Total Auth Attempts',
      value: overview?.totals?.totalAttempts,
      helper: `Across the last ${days} days`,
    },
    {
      label: 'Active Plugin Users',
      value: overview?.totals?.uniqueUsers,
      helper: 'Unique user IDs found in auth attempts',
    },
    {
      label: 'Products Seen',
      value: overview?.totals?.uniqueProducts,
      helper: 'Products with usage or licenses',
    },
    {
      label: 'Selected Product Users',
      value: selectedProductOverview?.uniqueUsers,
      helper: selectedProduct || 'Select a product',
    },
  ];

  return (
    <>
      <Seo
        title="Plugin Usage | Admin | Parish Audio"
        description="View Parish Audio plugin usage and retention."
        canonical="https://parishaudio.com/admin/plugin-usage"
        robots="noindex,nofollow"
      />

      <Box
        sx={{
          backgroundColor: 'black',
          minHeight: '100vh',
          color: style.color4,
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 2, md: 3 },
        }}
      >
        <Box sx={{ maxWidth: 1450, mx: 'auto' }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: '20px',
              overflow: 'hidden',
              backgroundColor: style.color1,
              border: `1px solid ${style.color2}`,
            }}
          >
            <Box
              sx={{
                px: { xs: 1.5, sm: 2, md: 3 },
                py: { xs: 2.25, md: 3 },
                backgroundColor: panelBg,
                borderBottom: `1px solid ${style.color2}`,
              }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
              >
                <Stack spacing={1} alignItems="flex-start">
                  <Chip
                    icon={<AnalyticsRoundedIcon />}
                    label="Admin Analytics"
                    sx={{
                      borderRadius: '5px',
                      fontWeight: 700,
                      color: style.color4,
                      backgroundColor: 'rgba(255,255,255,0.06)',
                    }}
                  />

                  <Typography
                    sx={{
                      color: style.color4,
                      fontSize: { xs: '1.9rem', md: '2.5rem' },
                      fontWeight: 800,
                      lineHeight: 1.05,
                      letterSpacing: '-0.03em',
                    }}
                  >
                    Plugin Usage
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.65 }}>
                    See usage by app, repeat usage, retention, active days, and users who have logged into plugins on devices.
                  </Typography>
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button
                    startIcon={<ArrowBackRoundedIcon />}
                    href="/admin"
                    sx={{
                      borderRadius: '5px',
                      textTransform: 'none',
                      fontWeight: 700,
                      color: style.color4,
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.08)',
                    }}
                  >
                    Admin
                  </Button>

                  <TextField
                    select
                    label="Range"
                    value={days}
                    onChange={(event) => setDays(Number(event.target.value))}
                    size="small"
                    sx={sharedTextFieldSx}
                  >
                    <MenuItem value={30}>Last 30 days</MenuItem>
                    <MenuItem value={60}>Last 60 days</MenuItem>
                    <MenuItem value={90}>Last 90 days</MenuItem>
                    <MenuItem value={180}>Last 180 days</MenuItem>
                    <MenuItem value={365}>Last year</MenuItem>
                  </TextField>

                  <Button
                    startIcon={<RefreshRoundedIcon />}
                    onClick={() => {
                      loadOverview();
                      loadDetail();
                    }}
                    disabled={loadingOverview || loadingDetail}
                    sx={{
                      borderRadius: '5px',
                      textTransform: 'none',
                      fontWeight: 700,
                      color: style.color4,
                      backgroundColor: style.accentGreen,
                    }}
                  >
                    Refresh
                  </Button>
                </Stack>
              </Stack>
            </Box>

            <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, py: 2.5 }}>
              <Stack spacing={2}>
                <Grid container spacing={2}>
                  {statCards.map((card) => (
                    <Grid item xs={12} sm={6} md={3} key={card.label}>
                      <Paper elevation={0} sx={sectionCardSx}>
                        <Typography sx={{ color: style.color3, fontWeight: 700, fontSize: '.85rem' }}>
                          {card.label}
                        </Typography>

                        <Typography sx={{ color: style.color4, fontWeight: 900, fontSize: '2rem', lineHeight: 1.1 }}>
                          {formatNumber(card.value)}
                        </Typography>

                        <Typography sx={{ color: style.color3, fontSize: '.85rem' }}>
                          {card.helper}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>

                <Paper elevation={0} sx={sectionCardSx}>
                  <Stack spacing={1.5}>
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', md: 'center' }}
                      spacing={1}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <DevicesRoundedIcon sx={{ color: style.color3 }} />
                        <Typography sx={{ color: style.color4, fontWeight: 800 }}>
                          Usage by App
                        </Typography>
                      </Stack>

                      <TextField
                        select
                        label="Drill Down"
                        value={selectedProduct}
                        onChange={(event) => setSelectedProduct(event.target.value)}
                        size="small"
                        sx={{ ...sharedTextFieldSx, minWidth: { xs: '100%', sm: 260 } }}
                      >
                        {(overview?.products || []).map((product) => (
                          <MenuItem value={product.productName} key={product.productName}>
                            {product.productName}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Stack>

                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={headerCellSx}>Product</TableCell>
                            <TableCell sx={headerCellSx} align="right">Users</TableCell>
                            <TableCell sx={headerCellSx} align="right">Auths</TableCell>
                            <TableCell sx={headerCellSx} align="right">Active Days/User</TableCell>
                            <TableCell sx={headerCellSx} align="right">Users w/ Device</TableCell>
                            <TableCell sx={headerCellSx} align="right">Devices</TableCell>
                            <TableCell sx={headerCellSx}>Last Seen</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {(overview?.products || []).map((product) => (
                            <TableRow
                              key={product.productName}
                              hover
                              onClick={() => setSelectedProduct(product.productName)}
                              sx={{
                                cursor: 'pointer',
                                backgroundColor:
                                  product.productName === selectedProduct
                                    ? 'rgba(255,255,255,0.06)'
                                    : 'transparent',
                              }}
                            >
                              <TableCell sx={{ ...tableCellSx, fontWeight: 800 }}>
                                {product.productName}
                              </TableCell>

                              <TableCell sx={tableCellSx} align="right">
                                {formatNumber(product.uniqueUsers)}
                              </TableCell>

                              <TableCell sx={tableCellSx} align="right">
                                {formatNumber(product.totalAttempts)}
                              </TableCell>

                              <TableCell sx={tableCellSx} align="right">
                                {product.avgActiveDaysPerUser || 0}
                              </TableCell>

                              <TableCell sx={tableCellSx} align="right">
                                {formatNumber(product.usersWithDevice)}
                              </TableCell>

                              <TableCell sx={tableCellSx} align="right">
                                {formatNumber(product.installedDeviceCount)}
                              </TableCell>

                              <TableCell sx={tableCellSx}>
                                {formatDate(product.lastSeenAt)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Stack>
                </Paper>

                {detail && (
                  <Paper elevation={0} sx={sectionCardSx}>
                    <Stack spacing={1.5}>
                      <Typography sx={{ color: style.color4, fontWeight: 800 }}>
                        Retention: {detail.productName}
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography sx={{ color: style.color3 }}>
                            Returned After First Day
                          </Typography>

                          <Typography sx={{ color: style.color4, fontWeight: 900, fontSize: '1.8rem' }}>
                            {percentText(detail.retention?.returnedAfterFirstDayRate)}
                          </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Typography sx={{ color: style.color3 }}>
                            Returned Within 7 Days
                          </Typography>

                          <Typography sx={{ color: style.color4, fontWeight: 900, fontSize: '1.8rem' }}>
                            {percentText(detail.retention?.returnedWithin7DaysRate)}
                          </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Typography sx={{ color: style.color3 }}>
                            Day 7+ Retention
                          </Typography>

                          <Typography sx={{ color: style.color4, fontWeight: 900, fontSize: '1.8rem' }}>
                            {percentText(detail.retention?.retainedDay7Rate)}
                          </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Typography sx={{ color: style.color3 }}>
                            Day 30+ Retention
                          </Typography>

                          <Typography sx={{ color: style.color4, fontWeight: 900, fontSize: '1.8rem' }}>
                            {percentText(detail.retention?.retainedDay30Rate)}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

                      <Typography sx={{ color: style.color4, fontWeight: 800 }}>
                        Recent Users
                      </Typography>

                      <TableContainer sx={{ maxHeight: 520 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={headerCellSx}>Email</TableCell>
                              <TableCell sx={headerCellSx} align="right">Active Days</TableCell>
                              <TableCell sx={headerCellSx} align="right">Auths</TableCell>
                              <TableCell sx={headerCellSx} align="right">Days Since Last Use</TableCell>
                              <TableCell sx={headerCellSx} align="right">Devices</TableCell>
                              <TableCell sx={headerCellSx}>First Use</TableCell>
                              <TableCell sx={headerCellSx}>Last Use</TableCell>
                              <TableCell sx={headerCellSx}>Returned?</TableCell>
                            </TableRow>
                          </TableHead>

                          <TableBody>
                            {(detail.users || []).map((user) => (
                              <TableRow key={user.userID}>
                                <TableCell sx={{ ...tableCellSx, minWidth: 220 }}>
                                  <Typography sx={{ color: style.color4, fontWeight: 800, fontSize: '.9rem' }}>
                                    {user.email || 'No email found'}
                                  </Typography>

                                  {user.name && (
                                    <Typography sx={{ color: style.color3, fontSize: '.8rem' }}>
                                      {user.name}
                                    </Typography>
                                  )}
                                </TableCell>

                                <TableCell sx={tableCellSx} align="right">
                                  {formatNumber(user.activeDays)}
                                </TableCell>

                                <TableCell sx={tableCellSx} align="right">
                                  {formatNumber(user.attempts)}
                                </TableCell>

                                <TableCell sx={tableCellSx} align="right">
                                  {formatNumber(user.daysSinceLastUse)}
                                </TableCell>

                                <TableCell sx={tableCellSx} align="right">
                                  {formatNumber(user.installedDeviceCount)}
                                </TableCell>

                                <TableCell sx={tableCellSx}>
                                  {formatDate(user.firstAttemptAt)}
                                </TableCell>

                                <TableCell sx={tableCellSx}>
                                  {formatDate(user.lastAttemptAt)}
                                </TableCell>

                                <TableCell sx={tableCellSx}>
                                  {user.returnedAfterFirstDay ? 'Yes' : 'No'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Stack>
                  </Paper>
                )}
              </Stack>
            </Box>
          </Paper>
        </Box>
      </Box>
    </>
  );
}